import { useEffect } from "react";

import { Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import { MediaType } from "@api/graphql/types-and-hooks";
import { FeedMediaCard } from "@pages/VideoSessionsV2/components/Media/FeedMediaCard";
import { FeedSkeletonLoader } from "@pages/VideoSessionsV2/components/Media/FeedSkeletonLoader";
import { useAdminData } from "@providers/AdminDataProvider";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

type EmptyStateContent = {
  headline: string;
  subtext: string;
};

export type MediaCardType = Pick<
  MediaType,
  | "id"
  | "mediaType"
  | "totalDuration"
  | "mediaStatus"
  | "createdDate"
  | "videoSession"
  | "commentsCount"
  | "mediaClassifications"
  | "isViewed"
  | "signedUploadUrl"
  | "thumbnailManifest"
>;

export const MediaCards: React.FC = () => {
  const {
    setAllFilteredMedias,
    allFilteredMedias,
    filterIsReviewed,
    setIsMediaLoading,
    isFilterActive,
    isMediaLoading,
    getMediaQuery,
    deletedMediaIds,
    viewedMediaMap,
  } = useVideoSessionData();
  const { isUserRBT } = useAdminData();

  const {
    data: allMediaData,
    isFetchingNextPage: isMediaNextPageFetching,
    isFetching: isMediaFetching,
    hasNextPage: isMediaHasNextPage,
    fetchNextPage: fetchMediaNextPage,
    isError,
  } = getMediaQuery;

  useEffect(() => {
    if (isMediaFetching && !isMediaNextPageFetching) {
      setIsMediaLoading(true);
    } else {
      setIsMediaLoading(false);
    }
  }, [isMediaFetching, isMediaNextPageFetching]);

  useEffect(() => {
    if (isMediaHasNextPage && !isMediaNextPageFetching) {
      fetchMediaNextPage();
    }
  }, [isMediaHasNextPage, isMediaNextPageFetching, fetchMediaNextPage]);

  useEffect(() => {
    if (allMediaData) {
      const dataTemp = allMediaData.pages
        .map((group) => {
          // @ts-ignore: Ignoring the compiler and risking bugs because: haking value here to split
          return [...group.edges];
        })
        .flat();
      const displayedMedia =
        dataTemp
          .map((item) => item?.node as MediaCardType)
          .filter(Boolean)
          .filter((media) => !deletedMediaIds.has(media.id))
          .map((media) => {
            const isViewed = viewedMediaMap.hasOwnProperty(media.id) ? viewedMediaMap[media.id] : media.isViewed;
            return { ...media, isViewed };
          })
          .filter((media) => {
            if (filterIsReviewed === false && media.isViewed) return false;
            if (filterIsReviewed === true && !media.isViewed) return false;
            return true;
          }) ?? [];
      setAllFilteredMedias(displayedMedia);
    }
  }, [allMediaData, deletedMediaIds, filterIsReviewed, viewedMediaMap]);

  const getEmptyStateContent = (): EmptyStateContent => {
    const isReviewedTab = filterIsReviewed === true;

    // Scenario 0: Filters are applied and no videos found
    if (isFilterActive && allFilteredMedias.length === 0) {
      return {
        headline: "No videos match your filters right now.",
        subtext:
          "It looks like there aren’t any videos that meet the selected criteria. Try adjusting or clearing some of the filters to explore more content in the video feed.",
      };
    }

    // Scenario 2: In Reviewed tab but no reviewed videos yet
    if (isReviewedTab && allFilteredMedias.length === 0 && !isFilterActive) {
      return {
        headline: "You haven’t reviewed any videos yet.",
        subtext:
          "Videos that you mark as reviewed will show up here for easy reference and organization of your viewing progress.",
      };
    }

    // Scenario 1 or default: No videos at all, no filters applied
    return {
      headline: "No video recordings yet!",
      subtext: isUserRBT
        ? "Once you record and fully upload a session from your video recording tablet, it will appear here. Check back after you’ve recorded and uploaded your first video!"
        : "It looks like no videos have been uploaded yet. Some recordings may still be in progress, but once your RBT completes an upload, their video sessions will appear here.",
    };
  };

  if (isMediaLoading && !isMediaNextPageFetching) {
    return <FeedSkeletonLoader />;
  }

  if (isError) {
    return <div>Error loading media</div>;
  }

  return (
    <div className="grid gap-4">
      <div
        className={`grid ${allFilteredMedias.length ? "mobile:grid-cols-1 tablet:grid-cols-2 web:grid-cols-3 items-start" : "grid-cols-1"} gap-4`}
      >
        {allFilteredMedias.length ? (
          allFilteredMedias.map((media) => <FeedMediaCard media={media} key={media.id} />)
        ) : (
          <div className="flex flex-col pt-[80px] items-center justify-center">
            <div className="flex flex-col gap-3 items-center justify-center max-w-[450px]">
              <Headline size="medium">{getEmptyStateContent().headline}</Headline>
              <Paragraph>{getEmptyStateContent().subtext}</Paragraph>
            </div>
          </div>
        )}
      </div>
      {isMediaNextPageFetching ? <FeedSkeletonLoader /> : null}
    </div>
  );
};
